import { defineMessages } from '@alltrails/shared/react-intl';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import Link from '@alltrails/shared/denali/components/Link';
import useExperiments from 'hooks/useExperiments';
import { useCallback, useMemo } from 'react';
import * as styles from './styles/styles.module.scss';

const EDIT_HEADER_STRINGS = defineMessages({
  CREATE_A_CUSTOM_MAP: { defaultMessage: 'Create a custom map' },
  EASILY_PLAN_THE_PERFECT_PATH: { defaultMessage: 'Easily plan the perfect path for your next hike, bike, or run with our Map Creator tool.' },
  TITLE: { defaultMessage: 'Legacy map builder' },
  FREE_BYOT_TITLE: { defaultMessage: 'Legacy route builder' },
  SUBTEXT: { defaultMessage: 'We’re no longer updating this tool, but you can keep using it for free. Or <link>try Custom Routes</link> with Peak!' },
  FREE_BYOT_SUBTEXT: {
    defaultMessage: 'The new free <link>Custom Routes</link> tool is here! You can keep using this older version until January 31.'
  },
  FREE_BYOT_SURVEY_LINK: { defaultMessage: 'Like this version? <link>Tell us why</link>' }
});

const EditHeader = () => {
  const {
    formatMessage,
    formattedDefaultMessages: { TITLE, CREATE_A_CUSTOM_MAP, EASILY_PLAN_THE_PERFECT_PATH, FREE_BYOT_TITLE }
  } = useFormatMessage(EDIT_HEADER_STRINGS);

  const experiments = useExperiments();
  const isPhase2CustomRoutes = experiments['web-discover-byot-phase2-rm']?.value === 'treatment';
  const hasFreeBYOT = experiments['web-discover-byot-free-rm']?.value === 'on';

  const LinkComponent = useCallback(
    (text: string) => (
      <Link href={`/explore/custom-routes/new?returnTo=${window.location.pathname}`} variant="primary">
        {text}
      </Link>
    ),
    []
  );

  const FreeLinkComponent = useCallback(
    (text: string) => (
      <Link className={styles.freeLink} href={`/explore/custom-routes/new?returnTo=${window.location.pathname}`} variant="primary">
        {text}
      </Link>
    ),
    []
  );

  const FreeSurveyComponent = useCallback(
    (text: string) => (
      <Link
        className={styles.freeLink}
        href="https://alltrails.qualtrics.com/jfe/form/SV_cZypMMM4otdbQR8"
        rel="noreferrer"
        target="_blank"
        variant="primary"
      >
        {text}
      </Link>
    ),
    []
  );

  const formattedSubtextMessage = useMemo(() => formatMessage('SUBTEXT', { link: LinkComponent }), [formatMessage, LinkComponent]);

  if (hasFreeBYOT) {
    return (
      <header className={styles.header}>
        <h1 className={styles.title}>{FREE_BYOT_TITLE}</h1>
        <div className={styles.freeSubtext}>
          <p>{formatMessage('FREE_BYOT_SUBTEXT', { link: FreeLinkComponent })}</p>
          <p className={styles.surveyText}>{formatMessage('FREE_BYOT_SURVEY_LINK', { link: FreeSurveyComponent })}</p>
        </div>
      </header>
    );
  }

  const headerContent = isPhase2CustomRoutes
    ? { title: TITLE, subtext: formattedSubtextMessage }
    : { title: CREATE_A_CUSTOM_MAP, subtext: EASILY_PLAN_THE_PERFECT_PATH };

  return (
    <header className={styles.header}>
      <h1 className={styles.title}>{headerContent.title}</h1>
      <p className={styles.subtext}>{headerContent.subtext}</p>
    </header>
  );
};

export default EditHeader;
